import { useQuery } from '@tanstack/react-query';
import { http } from 'services/http';
import { CARGOS_GET_MANY, CARGOS_SEARCH_HINTS } from 'api/query-keys.ts';
import type { IGetCargoRes } from 'api/swagger';
import { PAGINATION_DEFAULT_LIMIT } from 'shared/constants/paginations';
import { setStatusFiltersToQs, useCargoStatusFilters } from 'shared/hooks/use-cargo-status-filters';
import type { ICargoFilter } from 'shared/stores/use-cargo-filters';
import { setFiltersToQs } from 'shared/utils/filters/set-filters-to-qs';
import type { WithPagination } from 'shared/utils/pagination';
import { preparePagination } from 'shared/utils/pagination';
import type { IWithPagination } from 'types/api-query';

interface IParams extends IWithPagination {
  search?: string;
  filters?: ICargoFilter;
  isHints?: boolean;
  isSkip?: boolean;
  includeFromStatuses?: boolean;
}

export const useGetManyCargos = (params?: IParams) => {
  const {
    limit = PAGINATION_DEFAULT_LIMIT,
    page = 1,
    search,
    includeFromStatuses = false,
    filters,
    isSkip,
    isHints = false,
  } = params || {};

  const { statusFilters, filtersWithoutStatuses } = useCargoStatusFilters({ filters, includeFromStatuses });

  const qs = new URLSearchParams({ limit: String(limit), page: String(page) });
  if (search) qs.set('filter[search]', search);
  if (filters) setFiltersToQs<ICargoFilter>(qs, filtersWithoutStatuses);

  setStatusFiltersToQs(qs, statusFilters);

  return useQuery<WithPagination<IGetCargoRes[]>>({
    queryKey: isHints ? [CARGOS_SEARCH_HINTS, search] : [CARGOS_GET_MANY, { limit, page, search, filters }],
    queryFn: async () => {
      const { data, headers } = await http.get<IGetCargoRes[]>(`/v1/cargo?${qs.toString()}`);
      const pagination = preparePagination(headers);

      return { data, pagination };
    },
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 0,
    cacheTime: 0,
    enabled: !isSkip,
  });
};
